import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutGrid, UserPlus, X, Send } from 'lucide-react';
import { FORCEP } from './projects';

/*
  SECTION 3 — WELCOME TO THE FACTORY (1:1 with Illustrator master)
  Cream stage + retro stripe bands + central dock with the two actions:
   · KNOW OUR SERVICES  → existing /services pipeline
   · GENERA UNA CONSULTA → opens the consultation cockpit (form)
  Black basement with the cream Forcep isotype.
*/

const STRIPES = ['#D9A441', '#C2682C', '#A93B25', '#6E2218'];

const PIPELINE_OPTIONS = [
  'N1 · DISEÑO Y FUNDACIÓN — Estrategia & viabilidad',
  'N2 · PRODUCCIÓN Y DESARROLLO — Software / Pure Fábrica / AI',
  'N3 · DISTRIBUCIÓN & GROWTH — Social, ads, eventos IRL',
  'N4 · TELEMETRÍA & CONTROL — Data, optimización, SLA',
  'VENTURE COMPLETO — Los 4 nodos',
];

const Stripes = ({ side }) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none absolute bottom-0 ${side === 'left' ? 'left-0' : 'right-0'} flex w-[22%] flex-col`}
  >
    {STRIPES.map((c) => (
      <div key={c} style={{ backgroundColor: c }} className="h-7 w-full md:h-9" />
    ))}
  </div>
);

const ConsultaCockpit = ({ open, onClose }) => {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', brand: '', node: PIPELINE_OPTIONS[0], budget: '', brief: '' });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const body = encodeURIComponent(
      `OPERATIVE: ${form.name}\nCOMMS: ${form.email}\nMARCA/PROYECTO: ${form.brand}\nNODO: ${form.node}\nPRESUPUESTO: ${form.budget}\n\nMISSION BRIEF:\n${form.brief}`
    );
    window.location.href = `mailto:info@forcepfabrica.art?subject=${encodeURIComponent('CONSULTA — ' + form.brand)}&body=${body}`;
    setSent(true);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-end justify-center bg-black/60 backdrop-blur-sm md:items-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 120, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 120, scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 220, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="landing-font w-full max-w-2xl rounded-t-[2rem] border-[3px] border-black bg-[#f1ece4] p-6 shadow-2xl md:rounded-[2rem] md:p-8"
            role="dialog"
            aria-modal="true"
            aria-label="Genera una consulta"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="flex items-center gap-2 text-[11px] font-bold tracking-[0.25em] text-black/60">
                  <UserPlus className="h-4 w-4" /> CONTACT US NOW
                </p>
                <h3 className="mt-1 text-[24px] font-bold tracking-tight text-black md:text-[30px]">GENERA UNA CONSULTA</h3>
              </div>
              <button type="button" onClick={onClose} aria-label="Cerrar" className="rounded-full bg-black p-2 text-white transition-transform hover:scale-110">
                <X className="h-4 w-4" />
              </button>
            </div>

            {sent ? (
              <div className="mt-8 rounded-2xl bg-black p-8 text-center text-white">
                <p className="text-[14px] font-bold tracking-widest">PROTOCOLO TRANSMITIDO</p>
                <p className="mt-2 text-[13px] text-white/70">
                  Tu cliente de correo se abrió con el brief. Si prefieres directo:{' '}
                  <a className="underline" href="https://wa.me/5215563335612" target="_blank" rel="noreferrer">WhatsApp +52 55 6333 5612</a>
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
                <input required value={form.name} onChange={set('name')} placeholder="NOMBRE OPERATIVO"
                  className="cockpit-input" />
                <input required type="email" value={form.email} onChange={set('email')} placeholder="CANAL DE COMMS (EMAIL)"
                  className="cockpit-input" />
                <input required value={form.brand} onChange={set('brand')} placeholder="MARCA / PROYECTO"
                  className="cockpit-input" />
                <input value={form.budget} onChange={set('budget')} placeholder="PRESUPUESTO (USD/MXN)"
                  className="cockpit-input" />
                <select value={form.node} onChange={set('node')} className="cockpit-input md:col-span-2">
                  {PIPELINE_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
                <textarea required value={form.brief} onChange={set('brief')} placeholder="MISSION BRIEF — cuéntanos qué construimos"
                  rows={4} className="cockpit-input md:col-span-2" />
                <button
                  type="submit"
                  className="md:col-span-2 mt-1 flex items-center justify-center gap-2 rounded-xl bg-black px-6 py-4 text-[13px] font-bold tracking-[0.2em] text-white transition-transform hover:scale-[1.01]"
                >
                  TRANSMITIR PROTOCOLO <Send className="h-4 w-4" />
                </button>
                <p className="md:col-span-2 text-center text-[10px] text-black/40">
                  Vía email a info@forcepfabrica.art · WhatsApp directo +52 55 6333 5612
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const FactorySection = () => {
  const [cockpit, setCockpit] = useState(false);

  return (
    <section className="landing-font relative bg-black">
      {/* Cream stage */}
      <div className="relative mx-auto overflow-hidden rounded-b-[2.5rem] bg-[#f1ece4]">
        <div className="relative flex min-h-[58svh] flex-col items-center justify-end pb-24 md:min-h-[64svh] md:pb-28">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="mb-10 px-6 text-center text-[24px] font-normal tracking-tight text-black md:mb-14 md:text-[34px]"
          >
            WELCOME TO THE FACTORY
          </motion.h2>
          <Stripes side="left" />
          <Stripes side="right" />
        </div>
      </div>

      {/* Dock — straddles the cream/black boundary */}
      <div className="relative z-20 mx-auto -mt-16 w-[92%] max-w-2xl md:-mt-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="rounded-[1.6rem] border-[3px] border-black bg-white p-3 shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Link
              to="/services"
              className="group rounded-[1.1rem] bg-black p-5 text-white transition-transform duration-200 hover:scale-[1.02]"
            >
              <LayoutGrid className="h-5 w-5" />
              <p className="mt-3 text-[15px] font-bold tracking-wide">KNOW OUR SERVICES</p>
              <p className="text-[9px] font-bold tracking-[0.2em] text-white/60">PRODUCTS &amp; COLLABORATIONS</p>
            </Link>
            <button
              type="button"
              onClick={() => setCockpit(true)}
              className="group rounded-[1.1rem] border border-black/15 bg-[#efe9df] p-5 text-left text-black transition-transform duration-200 hover:scale-[1.02]"
            >
              <UserPlus className="h-5 w-5" />
              <p className="mt-3 text-[15px] font-bold tracking-wide">GENERA UNA CONSULTA</p>
              <p className="text-[9px] font-bold tracking-[0.2em] text-black/50">CONTACT US NOW</p>
            </button>
          </div>
        </motion.div>
      </div>

      {/* Black basement with cream isotype */}
      <div className="flex items-center justify-center bg-black pb-20 pt-24 md:pb-28 md:pt-32">
        <motion.img
          initial={{ opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7 }}
          src={FORCEP.isoLight}
          alt="Forcep Fábrica"
          className="h-32 w-auto md:h-44"
        />
      </div>

      <ConsultaCockpit open={cockpit} onClose={() => setCockpit(false)} />
    </section>
  );
};

export default FactorySection;
